import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useSalesAnalytics } from "@/hooks/useSalesAnalytics";
import { 
  Crown, 
  Trophy, 
  Medal, 
  TrendingUp,
  Users,
  Star,
  Award
} from "lucide-react";

interface EmployeeRankingLeaderboardProps {
  timeFilter: string;
  departmentFilter: string;
}

export const EmployeeRankingLeaderboard = ({ timeFilter, departmentFilter }: EmployeeRankingLeaderboardProps) => {
  const { data: salesData, isLoading } = useSalesAnalytics();

  if (isLoading) {
    return (
      <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getRankIcon = (index: number) => {
    switch (index) {
      case 0: return <Crown className="w-6 h-6 text-yellow-500" />;
      case 1: return <Trophy className="w-6 h-6 text-gray-400" />;
      case 2: return <Medal className="w-6 h-6 text-amber-600" />;
      default: return (
        <div className="w-6 h-6 bg-slate-400 rounded-full flex items-center justify-center text-white text-sm font-bold">
          {index + 1}
        </div>
      );
    }
  };

  const getRankBg = (index: number) => {
    switch (index) {
      case 0: return "bg-gradient-to-r from-yellow-50 to-yellow-100 border-l-4 border-yellow-400";
      case 1: return "bg-gradient-to-r from-gray-50 to-gray-100 border-l-4 border-gray-400";
      case 2: return "bg-gradient-to-r from-amber-50 to-amber-100 border-l-4 border-amber-400";
      default: return "bg-gradient-to-r from-slate-50 to-slate-100 border-l-4 border-slate-300";
    }
  };

  const getPerformanceScore = (employee: any) => {
    // حساب نقاط الأداء بناءً على المبيعات والعدد
    const revenueScore = (employee.total / (salesData?.topEmployees?.[0]?.total || 1)) * 50;
    const volumeScore = (employee.count / Math.max(...(salesData?.topEmployees || []).map(e => e.count))) * 30;
    const averageScore = (employee.average / Math.max(...(salesData?.topEmployees || []).map(e => e.average))) * 20;
    return Math.min(Math.round(revenueScore + volumeScore + averageScore), 100);
  };

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-purple-600" />
          ترتيب الموظفين حسب الأداء
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {salesData?.topEmployees?.map((employee, index) => {
            const performanceScore = getPerformanceScore(employee);
            
            return (
              <div key={employee.name} className={`p-5 rounded-xl ${getRankBg(index)} hover:shadow-lg transition-all duration-200`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    {getRankIcon(index)}
                    <div>
                      <h3 className="font-bold text-lg text-slate-800">{employee.name}</h3>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {employee.department}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-current" />
                          <span className="text-xs text-slate-600">{performanceScore}/100</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-left">
                    <div className="text-2xl font-bold text-slate-800">
                      {employee.total.toLocaleString('ar-SA')} ر.س
                    </div>
                    <div className="text-sm text-slate-600 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {employee.count} معاملة
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-600">نقاط الأداء</span>
                    <span className="font-medium">{performanceScore}%</span>
                  </div>
                  <Progress value={performanceScore} className={`h-2 ${
                    index === 0 ? '[&>div]:bg-yellow-500' :
                    index === 1 ? '[&>div]:bg-gray-400' :
                    index === 2 ? '[&>div]:bg-amber-500' :
                    '[&>div]:bg-slate-400'
                  }`} />
                  
                  <div className="grid grid-cols-3 gap-4 pt-2 text-sm">
                    <div className="text-center">
                      <div className="font-semibold text-slate-800">
                        {employee.average.toLocaleString('ar-SA', { maximumFractionDigits: 0 })} ر.س
                      </div>
                      <div className="text-slate-500 text-xs">متوسط المعاملة</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-slate-800">
                        {Math.round((employee.total / (salesData?.totalRevenue || 1)) * 100)}%
                      </div>
                      <div className="text-slate-500 text-xs">من إجمالي المبيعات</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-slate-800">
                        #{index + 1}
                      </div>
                      <div className="text-slate-500 text-xs">الترتيب</div>
                    </div>
                  </div>
                </div>
                
                {/* إضافة شارات للمراكز الثلاثة الأولى */}
                {index < 3 && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="flex items-center justify-center">
                      <Badge className={`
                        ${index === 0 ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                          index === 1 ? 'bg-gray-100 text-gray-800 border-gray-300' :
                          'bg-amber-100 text-amber-800 border-amber-300'}
                      `}>
                        <Award className="w-3 h-3 ml-1" />
                        {index === 0 ? 'المركز الأول' : index === 1 ? 'المركز الثاني' : 'المركز الثالث'}
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          
          {(!salesData?.topEmployees || salesData.topEmployees.length === 0) && (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold mb-2">لا توجد بيانات أداء</h3>
              <p>لم يتم العثور على بيانات أداء للموظفين في الفترة المحددة</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};